import { getAgentConceptsInStep2Order } from './getAgentConceptsInStep2Order';
import type {
  AddedConcept,
  AnnotationAgentState,
  AnnotationUser,
  ConditionArea,
  HealthSystem,
} from '../state/createProjectSlice';
import type {
  Basics,
  ConceptGroup,
  CreateAnnotationProjectRequest,
  CreateProjectAnnotationAgent,
} from '../types/request';
import type { SupervisorResponse } from '../types/response';

interface BuildCreateProjectRequestInput {
  projectName: string;
  conditionArea: ConditionArea | null;
  healthSystem: HealthSystem | null;
  addedConcepts: AddedConcept[];
  selectedAnnotators: AnnotationUser[];
  selectedSupervisors: SupervisorResponse[];
  defaultAllocationMode: boolean;
  workflowId: string | null;
  // TODO: cleanup with cp_annotation_createproject_agent ff
  isAgentStepEnabled: boolean;
  annotationAgent: AnnotationAgentState;
  humanSharePercentages: Record<string, string>;
}

// TODO: cleanup with cp_annotation_createproject_agent ff
const buildAnnotationAgent = (
  addedConcepts: AddedConcept[],
  { modelId, concepts, sections }: AnnotationAgentState
): CreateProjectAnnotationAgent => ({
  // Step 5 keeps Next disabled until a model is chosen; the server rejects an empty one.
  modelId: modelId ?? '',
  concepts: getAgentConceptsInStep2Order(addedConcepts, concepts).map(
    ({ conceptGroupId, concept }) => ({
      conceptGroupId,
      sequence: concept.sequence,
      name: concept.name,
      synonyms: concept.synonyms,
      notes: concept.notes,
    })
  ),
  sections: sections.map(({ heading, body }) => ({ heading, body })),
});

/**
 * Builds the body of `POST /manage-projects/project` from the wizard state.
 *
 * `annotationAgent` is added only while the agent step is part of the session and its toggle
 * is on, and then in Custom mode each annotator carries `humanSharePercentage`; otherwise the
 * body has the same keys and values as a wizard without the agent step.
 */
export const buildCreateProjectRequest = ({
  projectName,
  conditionArea,
  healthSystem,
  addedConcepts,
  selectedAnnotators,
  selectedSupervisors,
  defaultAllocationMode,
  workflowId,
  isAgentStepEnabled,
  annotationAgent,
  humanSharePercentages,
}: BuildCreateProjectRequestInput): CreateAnnotationProjectRequest => {
  const basics: Basics = {
    projectName,
    conditionAreaId: conditionArea?.id ?? '',
    conditionAreaName: conditionArea?.label ?? '',
    healthSystemId: healthSystem?.id ?? '',
    healthSystemName: healthSystem?.label ?? '',
  };
  const conceptGroups: ConceptGroup[] = addedConcepts.flatMap(
    ({ conceptGroupId, conceptGroupName, requiredAttributes }) =>
      conceptGroupId ? [{ conceptGroupId, conceptGroupName, requiredAttributes }] : []
  );
  const request: CreateAnnotationProjectRequest = {
    basics,
    conceptGroups,
    annotators: selectedAnnotators,
    supervisors: selectedSupervisors,
    defaultAllocationMode,
    workflowId: workflowId ?? '',
  };
  if (isAgentStepEnabled && annotationAgent.enabled) {
    request.annotationAgent = buildAnnotationAgent(addedConcepts, annotationAgent);
    if (!defaultAllocationMode) {
      // Step 6 keeps Next disabled until every share is a valid whole number.
      request.annotators = selectedAnnotators.map((annotator) => ({
        ...annotator,
        humanSharePercentage: Number(humanSharePercentages[annotator.id]),
      }));
    }
  }
  return request;
};
